/**
 * Per-domain throttling for bulk downloads (offline reading). A 429 blocks the response's domain
 * for its Retry-After, or with exponential backoff when the server doesn't say. Requests to a
 * blocked domain fail at once with ThrottledError instead of being sent, and the offline
 * downloader's queue (offline/host-queue.ts) holds that domain's work back while other domains
 * continue.
 *
 * Throttle state lives for the page; it is shared by every request that checks it, so a block the
 * downloader caused also spares the interactive reads to the same host.
 */

const MIN_BACKOFF_MS = 30 * 1000;
const MAX_BACKOFF_MS = 15 * 60 * 1000;
/** Consecutive network errors after which a domain is backed off. Many hosts send their 429
 *  without CORS headers, so the browser reports it as a plain network error. */
const NETWORK_ERRORS_BEFORE_BACKOFF = 3;

interface ThrottleState {
  /** Blocked until then (ms epoch); in the past when not blocked. */
  until: number;
  /** The last backoff; doubles with every block until a request gets through. */
  backoffMs: number;
  networkErrors: number;
  /** Blocked because of network errors (not a 429): lifted when the browser comes back online. */
  soft: boolean;
}

const states = new Map<string, ThrottleState>();

/** A request was not sent, or was refused with 429, because its domain is throttled. */
export class ThrottledError extends Error {
  constructor(readonly key: string, readonly until: number) {
    super(`${key} is throttled until ${new Date(until).toISOString()}`);
  }
}

/**
 * The key a URL is throttled under. Our own hosts each have their own limit (every sync instance,
 * every CDN), so they are keyed by host. Anything else by domain: sites spread their images over
 * subdomains (i0.wp.com, i1.wp.com, …) behind one limit.
 */
export function throttleKey(url: string): string {
  let host: string;
  try {
    host = new URL(url).hostname;
  } catch {
    return '';
  }
  if (host == 'downread.com' || host.endsWith('.downread.com') || /^[\d.]+$|[:[]/.test(host)) return host;
  const labels = host.split('.');
  // Second-level registrations (example.co.uk, example.com.au) keep three labels.
  const n = labels.length > 2 && labels[labels.length - 1].length == 2 && labels[labels.length - 2].length <= 3 ? 3 : 2;
  return labels.slice(-n).join('.');
}

/** When the key's block ends, or 0 when it isn't blocked. */
export function throttledUntil(key: string, now = Date.now()): number {
  const until = states.get(key)?.until ?? 0;
  return until > now ? until : 0;
}

/** Throws ThrottledError when the URL's domain is blocked. Call before sending a request. */
export function throwIfThrottled(url: string): void {
  const key = throttleKey(url);
  const until = throttledUntil(key);
  if (until) throw new ThrottledError(key, until);
}

/** Milliseconds from a Retry-After header (delay-seconds or an HTTP date). */
export function parseRetryAfter(value: string | null | undefined, now = Date.now()): number | undefined {
  if (!value?.trim()) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(value);
  return isNaN(date) ? undefined : Math.max(0, date - now);
}

function block(key: string, retryAfterMs: number | undefined, soft: boolean): number {
  const state = states.get(key) ?? { until: 0, backoffMs: 0, networkErrors: 0, soft };
  state.backoffMs = Math.min(state.backoffMs * 2 || MIN_BACKOFF_MS, MAX_BACKOFF_MS);
  state.until = Date.now() + Math.min(retryAfterMs ?? state.backoffMs, MAX_BACKOFF_MS);
  state.soft = soft;
  states.set(key, state);
  return state.until;
}

/**
 * Note a response from the URL's domain. A 429 (or a 503 with Retry-After) blocks the domain and
 * throws ThrottledError; anything else shows the domain is reachable and resets its backoff.
 */
export function checkThrottled(response: Response, url: string): void {
  const key = throttleKey(url);
  const retryAfter = parseRetryAfter(response.headers?.get('retry-after'));
  if (response.status == 429 || (response.status == 503 && retryAfter != undefined))
    throw new ThrottledError(key, block(key, retryAfter, false));
  const state = states.get(key);
  if (!state) return;
  if (state.until > Date.now()) state.networkErrors = 0; // a request sent before the block
  else states.delete(key);
}

/** Note a failed request (network error, CORS) to the URL's domain; enough of them in a row
 *  back the domain off. */
export function noteNetworkError(url: string): void {
  const key = throttleKey(url);
  const state = states.get(key) ?? { until: 0, backoffMs: 0, networkErrors: 0, soft: true };
  states.set(key, state);
  if (++state.networkErrors < NETWORK_ERRORS_BEFORE_BACKOFF) return;
  state.networkErrors = 0;
  // A real 429 block stays in place; network errors only ever add a soft one.
  if (state.until <= Date.now() || state.soft) block(key, undefined, true);
}

/** Forget all throttle state. For tests. */
export function resetHostThrottles(): void {
  states.clear();
}

// Network errors while the device was offline say nothing about the hosts: lift those blocks.
if (typeof window != 'undefined')
  window.addEventListener('online', () => {
    states.forEach((state, key) => state.soft && states.delete(key));
  });
